import React, { useEffect, useState } from 'react';
import { useLang } from '../lib/langSettings';

// Odznak hodnocení obchodu v liště košíku. Zdroj (Heureka CZ/SK, Google) určuje administrace podle jazyka.
export type Quote = { text: string; author: string; date: string; score: number | null; source: string; url: string };
export type R = { source: 'off' | 'heureka' | 'google'; host?: string; rating?: number | null; count?: number | null; url?: string; quotes?: Quote[] };

export const LOCALE: Record<string, string> = { cs: 'cs-CZ', sk: 'sk-SK', pl: 'pl-PL', en: 'en-GB', de: 'de-DE', it: 'it-IT', es: 'es-ES', hu: 'hu-HU' };
// Text za počtem recenzí podle jazyka (n = počet, h = Heureka.cz / Heureka.sk / Google).
function countText(lang: string, n: number, h: string, nf: string) {
  const few = n >= 2 && n <= 4;
  switch (lang) {
    case 'cs': return `${nf} ${n === 1 ? 'recenze' : few ? 'recenze' : 'recenzí'} na ${h === 'Google' ? 'Googlu' : h}`;
    case 'sk': return `${nf} ${n === 1 ? 'recenzia' : few ? 'recenzie' : 'recenzií'} na ${h}`;
    case 'pl': return `${nf} ${n === 1 ? 'opinia' : few ? 'opinie' : 'opinii'} w ${h}`;
    case 'de': return `${nf} ${n === 1 ? 'Bewertung' : 'Bewertungen'} auf ${h}`;
    case 'it': return `${nf} ${n === 1 ? 'recensione' : 'recensioni'} su ${h}`;
    case 'es': return `${nf} ${n === 1 ? 'reseña' : 'reseñas'} en ${h}`;
    case 'hu': return `${nf} értékelés – ${h}`;
    default: return `${nf} ${n === 1 ? 'review' : 'reviews'} on ${h}`;
  }
}
const NO_COUNT: Record<string, string> = { cs: 'Recenze na', sk: 'Recenzie na', pl: 'Opinie w', de: 'Bewertungen auf', it: 'Recensioni su', es: 'Reseñas en', hu: 'Értékelések –', en: 'Reviews on' };

const cache: Record<string, Promise<R>> = {};
const loadReviews = (lang: string) => (cache[lang] ||= fetch(`/api/reviews?lang=${encodeURIComponent(lang)}`).then(r => r.ok ? r.json() : { source: 'off' }).catch(() => ({ source: 'off' })));

// Recenze pro aktuální jazyk zákazníka (jeden dotaz na jazyk, sdílený odznakem i úvodní obrazovkou).
export function useReviews(): [R | null, string] {
  const lang = useLang();
  const [r, setR] = useState<R | null>(null);
  useEffect(() => { let on = true; loadReviews(lang).then(v => { if (on) setR(v); }); return () => { on = false; }; }, [lang]);
  return [r, lang];
}

const ReviewBadge: React.FC<{ large?: boolean }> = ({ large }) => {
  const [r, lang] = useReviews();
  if (!r || r.source === 'off' || !r.url) return null;
  const loc = LOCALE[lang] || 'cs-CZ';
  const rating = typeof r.rating === 'number' ? r.rating.toLocaleString(loc, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '';
  const host = r.host || '';
  const text = typeof r.count === 'number' && r.count > 0 ? countText(lang, r.count, host, r.count.toLocaleString(loc)) : `${NO_COUNT[lang] || NO_COUNT.en} ${host}`;
  return (
    <a className={`rd-reviews rd-reviews--${r.source}${large ? ' rd-reviews--lg' : ''}`} href={r.url} target="_blank" rel="noopener noreferrer" translate="no" data-reviews-badge={r.source}>
      <span className="rd-reviews-star" aria-hidden="true">★</span>
      {rating && <strong>{rating}</strong>}
      <span>{text}</span>
    </a>
  );
};

export default ReviewBadge;
